import type { ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useI18n } from "@/lib/i18n/useI18n";

// ─── Mobile filter drawer ───────────────────────────────────────────────────────
// Bottom sheet used only below the xl breakpoint (the left rail is always
// visible above it) — a compact drawer instead of an inline chip block pushing
// the marketplace list down every time filters are open.
//
// Marketplace Filter Polish V2.1, task 3/4: header/subtitle and a sticky
// footer action area are now configurable so the drawer can carry
// marketplace-specific copy ("Filtrar experiencias") and the "Limpiar
// filtros"/"Ver resultados" actions without hardcoding them into this
// otherwise-generic shell.
export function MobileFilterDrawer({
  open,
  onClose,
  title,
  subtitle,
  footer,
  children,
}: {
  open:      boolean;
  onClose:   () => void;
  title?:    string;
  subtitle?: string;
  footer?:   ReactNode;
  children:  ReactNode;
}) {
  const { t } = useI18n();
  const resolvedTitle = title ?? t("marketplace.toolbar.filters");
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 xl:hidden"
            style={{ background: "oklch(0.10 0.010 55 / 0.38)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            aria-hidden
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={resolvedTitle}
            className="fixed inset-x-0 bottom-0 z-50 xl:hidden rounded-t-2xl overflow-hidden flex flex-col"
            style={{ background: "oklch(0.998 0.003 72)", maxHeight: "85vh" }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 320 }}
          >
            <div
              className="shrink-0 flex items-start justify-between gap-2 px-4 py-3"
              style={{ borderBottom: "1px solid oklch(0.880 0.024 72 / 0.20)" }}
            >
              <div className="min-w-0">
                <p className="text-[13px] font-bold" style={{ color: "var(--color-heading-navy)" }}>
                  {resolvedTitle}
                </p>
                {subtitle && (
                  <p className="text-[11px] mt-0.5 leading-snug" style={{ color: "oklch(0.545 0.020 60 / 0.55)" }}>
                    {subtitle}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label={t("marketplace.toolbar.filtersCloseAria")}
                className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full transition-opacity hover:opacity-70"
                style={{ color: "oklch(0.545 0.020 60 / 0.60)", background: "oklch(0.545 0.020 60 / 0.06)" }}
              >
                <X size={16} aria-hidden />
              </button>
            </div>

            <div className="min-h-0 flex-1 px-4 py-4 overflow-y-auto">
              {children}
            </div>

            {footer && (
              <div
                className="shrink-0 px-4 py-3"
                style={{ borderTop: "1px solid oklch(0.880 0.024 72 / 0.20)", background: "oklch(0.998 0.003 72)" }}
              >
                {footer}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
